import { useChat } from '@ai-sdk/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { v4 as uuid } from 'uuid';

import { ANSWER_MODE_AGUI_ENDPOINT } from 'api/constants';
import { useAppUIContext } from 'contexts/ui/appUIContext';
import { createUserMessage } from 'modules/shared/libs/chat/utils/chat.utils';
import {
  setAskKlueIsModernThread,
  setAskKlueThreadId,
} from 'store/views/dashboard';

import { useLazyGetAnswerThreadQuery } from '../api/answer-threads.api';
import { AnswerTransport } from '../transports/AnswerTransport';
import { convertAnswerApiMessagesToUIMessages } from '../utils/message-conversion.utils';

import type { AnswerThreadState } from '../api/answer-threads.api.types';
import type { ChatStatus, ChatUIMessage } from 'modules/shared/libs/chat/types';

export type UseAnswerChatReturnType = {
  messages: ChatUIMessage[];
  sendMessage: (text: string) => void;
  status: ChatStatus;
  error: Error | undefined;
  regenerate: () => void;
  stop: () => void;
  reset: () => void;
  isLoadingHistory: boolean;
  hasLoadError: boolean;
  threadId: string;
};

type UseAnswerChatOptions = {
  threadId?: string;
  disabledSources?: string[];
  previewingVisibilityGroup?: number | null;
};

const QueryParamConfig = {
  askKlueSessionId: withDefault(StringParam, undefined),
  askKlueSourceApi: withDefault(StringParam, undefined),
  askKlueCategory: withDefault(StringParam, undefined),
};

export function useAnswerChat({
  threadId,
  disabledSources,
  previewingVisibilityGroup,
}: UseAnswerChatOptions): UseAnswerChatReturnType {
  const [initialMessages, setInitialMessages] = useState<ChatUIMessage[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [, setQueryParams] = useQueryParams(QueryParamConfig);

  const transport = useMemo(
    () =>
      new AnswerTransport({
        api: ANSWER_MODE_AGUI_ENDPOINT,
        credentials: 'include',
      }),
    [],
  );

  // Update disabledSources on the transport instance when it changes
  useEffect(() => {
    transport.setDisabledSources(disabledSources);
  }, [transport, disabledSources]);

  // Update the view-as preview ID so the streaming chat URL gets the
  // matching query param (BE uses it to downgrade a curator request to
  // the consumer toolset for the previewed view).
  useEffect(() => {
    transport.setPreviewingVisibilityGroup(previewingVisibilityGroup);
  }, [transport, previewingVisibilityGroup]);

  const [fetchThread] = useLazyGetAnswerThreadQuery();
  const { toast } = useAppUIContext();

  // Load lifecycle:
  // - lastLoadedThreadIdRef advances ONLY on success — if the fetch errors,
  //   the threadId stays "unseen" so a later switch back to the same thread
  //   re-attempts (transient 500s/timeouts shouldn't permanently brick a
  //   conversation in this session).
  // - erroredThreadId mirrors the failed threadId to override the spinner so
  //   the user isn't stuck on a loading state — they get a toast and an
  //   empty body until they pick a different thread (or refresh).
  // - Derived isLoadingHistory keeps the spinner correct synchronously on
  //   the first render after threadId changes (no empty-body flicker).
  const lastLoadedThreadIdRef = useRef<string | undefined>();
  const [erroredThreadId, setErroredThreadId] = useState<string | undefined>();
  const isLoadingHistory = Boolean(
    threadId &&
      threadId !== lastLoadedThreadIdRef.current &&
      threadId !== erroredThreadId,
  );

  // Use the prop threadId when provided (viewing a specific thread),
  // fall back to currentThreadId (for new conversations), or generate UUID
  const effectiveThreadId = useMemo(
    () => threadId || currentThreadId || uuid(),
    [currentThreadId, threadId],
  );

  const {
    messages,
    sendMessage: sendMessageRaw,
    status,
    error,
    regenerate,
    stop,
    setMessages,
  } = useChat<ChatUIMessage>({
    id: effectiveThreadId,
    transport,
    messages: initialMessages,
  });

  // Load thread when threadId changes (e.g., restoring from sidebar).
  // RTK Query lazy triggers resolve to { data, error } and don't throw — use
  // .unwrap() so the catch fires and we can surface a toast on real failures.
  // The cancelled flag protects against out-of-order responses if the user
  // switches between threads quickly: a slower request for a previous thread
  // can resolve last and would otherwise clobber the active thread's state.
  useEffect(() => {
    if (!threadId) {
      lastLoadedThreadIdRef.current = undefined;
      return undefined;
    }
    if (threadId === lastLoadedThreadIdRef.current) return undefined;
    if (threadId === erroredThreadId) return undefined;

    let cancelled = false;
    const loadThread = async (targetThreadId: string) => {
      try {
        const threadData = await fetchThread({
          threadId: targetThreadId,
        }).unwrap();
        if (cancelled) return;

        if (threadData?.messages && threadData.messages.length > 0) {
          const loadedMessages = convertAnswerApiMessagesToUIMessages(
            threadData.messages,
          );
          setInitialMessages(loadedMessages);
          setMessages(loadedMessages);
        } else {
          setInitialMessages([]);
          setMessages([]);
        }
        if (threadData?.state) {
          transport.setThreadState(threadData.state as AnswerThreadState);
        }
        setCurrentThreadId(targetThreadId);
        lastLoadedThreadIdRef.current = targetThreadId;
      } catch (loadError) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to load thread:', loadError);
        toast.open({
          status: 'error',
          title: 'Ask Klue',
          message: 'Failed to load conversation. Please try again.',
        });
        setInitialMessages([]);
        setMessages([]);
        // Mark the threadId errored so the spinner clears, but leave the
        // ref unset so switching back later re-attempts the load.
        setErroredThreadId(targetThreadId);
      }
    };

    loadThread(threadId);
    return () => {
      cancelled = true;
    };
  }, [threadId, erroredThreadId, fetchThread, setMessages, transport, toast]);

  // Clear the errored marker once the user navigates to a different thread so
  // returning to the previously-failed one will re-attempt the load.
  useEffect(() => {
    if (erroredThreadId !== undefined && threadId !== erroredThreadId) {
      setErroredThreadId(undefined);
    }
  }, [threadId, erroredThreadId]);

  // Track whether we've already published the thread id back to dashboard
  // state so we only do it once per brand-new chat.
  const hasPublishedThreadIdRef = useRef(false);

  // Explicit reset function for starting a new chat
  const reset = useCallback(() => {
    setInitialMessages([]);
    setMessages([]);
    setCurrentThreadId(uuid());
    transport.setThreadState(undefined);
    hasPublishedThreadIdRef.current = false;
  }, [setMessages, transport]);

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      sendMessageRaw(createUserMessage(text));
    },
    [sendMessageRaw],
  );

  // Publish the generated thread id to dashboard state + URL only after the
  // backend has actually started responding (an assistant message has shown
  // up in the stream). Publishing on the user's send call instead would
  // point Redux/URL at a thread the backend never created if the first
  // request fails — refreshing or reopening would then 404 against an
  // imaginary thread and the sidebar refetch would race the conversation.
  useEffect(() => {
    if (hasPublishedThreadIdRef.current) return;
    if (threadId) return;
    if (!messages.some((m) => m.role === 'assistant')) return;

    setAskKlueThreadId(effectiveThreadId);
    setAskKlueIsModernThread(true);
    // askKlueSessionId/askKlueSourceApi/askKlueCategory drive the sidebar's
    // active-state and same-session checks; without them, clicking the
    // freshly created sidebar entry would trigger a redundant restore.
    setQueryParams(
      {
        askKlueSessionId: effectiveThreadId,
        askKlueSourceApi: 'answer-modern',
        askKlueCategory: 'askklue',
      },
      'replaceIn',
    );
    hasPublishedThreadIdRef.current = true;
  }, [messages, threadId, effectiveThreadId, setQueryParams]);

  // True when the load for the current threadId failed. Consumers gate input
  // on this so the user can't submit against a transport that never received
  // setThreadState — otherwise sends would fall back to the default
  // CURRENT_COMPETE_ANSWER state
  const hasLoadError = Boolean(threadId && threadId === erroredThreadId);

  return {
    messages,
    sendMessage,
    status,
    error,
    regenerate,
    stop,
    reset,
    isLoadingHistory,
    hasLoadError,
    threadId: effectiveThreadId,
  };
}
